import type { ReactNode } from "react";

import type { ChatTurnRow } from "@/contexts/ChatContext";
import { splitInterruptedTurn } from "@/lib/chatTurnPolicy";

import ChatBlockRenderer, { type ChatRenderableBlock } from "./ChatBlockRenderer";
import { DEGRADE_NOTE } from "./chatCopy";

/**
 * One turn, rendered.
 *
 * THE BUBBLE CHROME IS NOT HERE. It lives in `ChatBlockRenderer`, because the width, padding, radius
 * and surface of a rendered block are properties of the BLOCK (a text block sits in a bubble, a Phase
 * 39 carousel escapes it into a full-bleed row) rather than of the turn. What this file owns is
 * everything that belongs to the turn's LIFECYCLE: which side it sits on, whether it is still
 * optimistic, the caret while it grows, and the two footers.
 *
 * THE FOOTERS RENDER INSIDE THE SAME BUBBLE ELEMENT AS THE BODY TEXT. They are passed down as the
 * `footer` slot rather than appended as a sibling row, and that is the whole of D-08: the visitor was
 * reading mid-sentence, and the partial they had must not re-render into a new node, reflow, or move.
 */

/** 16px between turns, 8px between the blocks of one turn (design contract, Vertical rhythm). */
const TURN_BLOCK_GAP = "gap-2";

/** 13px/400/1.5 in `--ys-ink-2`. Both footers, and the only Label-role text inside a bubble. */
const FOOTER_TEXT_STYLE = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.5,
  color: "var(--ys-ink-2)",
} as const;

/**
 * Narrow the wire's `blocks` value to something renderable, or hand back `null`.
 *
 * `null` is not a failure path, it is the NORMAL path for this phase: v1 answers carry no envelope at
 * all, and the turn's `content` is authoritative in every case. Any member that is not an object with
 * a string `block.kind` and a string `text` fails the whole array rather than half of it, because
 * rendering three of five blocks is a silently truncated answer, whereas falling back to `content`
 * shows the visitor everything the server said.
 *
 * `items` IS NOT PART OF THAT TEST. The raw per-block payload is carried through untouched, and an
 * absent or unusable `items` must NEVER null the array. D-15's drop-the-bad-item rule sits one level
 * BELOW this one and does not contradict it: a `stores` block whose items all fail the allowlist is
 * still a VALID block that renders its own `text` fallback, not a `null` that collapses the whole
 * turn into `content`. The narrowing of `items` itself belongs to `toSafeCards`, not here.
 */
const toRenderableBlocks = (blocks: unknown): ChatRenderableBlock[] | null => {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  const renderable: ChatRenderableBlock[] = [];
  for (const candidate of blocks) {
    if (candidate === null || typeof candidate !== "object") return null;
    const { kind, text, items } = candidate as {
      kind?: unknown;
      text?: unknown;
      items?: unknown;
    };
    if (typeof kind !== "string" || typeof text !== "string") return null;
    // Conditional spread, so a block that carries no `items` puts no `items` key on the renderable.
    renderable.push({ kind, text, ...(items === undefined ? {} : { items }) });
  }
  return renderable;
};

/**
 * A muted footer with a 1px top rule.
 *
 * NO ICON, NO COLOUR, NO BADGE. The separation from the body text above is carried by the rule and by
 * the size step, which is exactly the "visible but subtle" the phase's tone target asks for. A badge
 * would make a stopped answer look like an error, and it was not an error: the visitor asked for it.
 */
const BubbleFooter = ({ children }: { children: ReactNode }) => (
  <div
    className="mt-2 border-t border-[var(--ys-rule)] pt-2"
    style={FOOTER_TEXT_STYLE}
  >
    {children}
  </div>
);

export default function ChatMessageBubble({ turn }: { turn: ChatTurnRow }) {
  // D-08's peel, decided by `chatTurnPolicy` and not re-derived here. The marker rides inside
  // `content` on its own line (appended server-side so it replays into model history for free), so a
  // REHYDRATED stopped turn already carries it and the panel must never append one of its own: doing
  // that would show the marker twice after a reload. The policy module gates the peel on the turn's
  // intent rather than on the marker's wording, and its suite covers the empty-partial and
  // absent-intent branches.
  const { body, marker } = splitInterruptedTurn({
    content: turn.content,
    intent: turn.intent,
  });

  // The envelope is ignored on a stopped turn. Plan 38-11 persists no envelope for one (a partial plus
  // an operational marker is not a composed answer), so this is belt: if one ever arrives, `content`
  // is still the string that carries the marker.
  const blocks = marker === null ? toRenderableBlocks(turn.blocks) : null;
  const parts: ChatRenderableBlock[] = blocks ?? [{ kind: 'text', text: body }];

  const isUser = turn.role === "user";

  // THE CARET, and it trails the last glyph rather than sitting in a row of its own. 2px wide, 1em
  // tall, `--ys-orange-600` (3.28:1 on the bot bubble, which clears SC 1.4.11). It is removed the
  // instant the turn terminates in ANY way -- done, interrupted, or a stream that dropped -- because
  // `streaming` is what the context clears on every terminal path. Under reduced motion the stylesheet
  // holds it SOLID rather than removing it: a static caret still says "more is coming", and deleting
  // it would strip that information from exactly the visitors who asked for less motion.
  const caret =
    turn.streaming === true ? (
      <span
        aria-hidden="true"
        className="animate-caret-blink ms-[2px] inline-block h-[1em] w-[2px] bg-[var(--ys-orange-600)]"
        style={{ verticalAlign: "-0.15em" }}
      />
    ) : null;

  // The degrade note is disclosed only because the signal ARRIVED for this turn (D-17), which only
  // happens when a deep tool was actually refused mid-run. There is no always-on banner and no panel
  // chrome for it. Suppressed when the bubble has no body text of its own: in that case the server's
  // own "cannot search widely right now" message is the whole bubble, and stacking a second
  // disclosure on top of it would say the same thing twice in two registers.
  const showDegradeNote = turn.degraded === true && body.trim() !== "";

  const footer =
    marker === null && !showDegradeNote ? null : (
      <>
        {marker === null ? null : <BubbleFooter>{marker}</BubbleFooter>}
        {showDegradeNote ? <BubbleFooter>{DEGRADE_NOTE}</BubbleFooter> : null}
      </>
    );

  return (
    <div
      className={`flex flex-col ${TURN_BLOCK_GAP} ${isUser ? "items-end" : "items-start"} ${
        // The optimistic bubble sits at reduced opacity until the server's FIRST frame of any kind,
        // which is the only thing that opacity was ever claiming: that we do not yet know the message
        // arrived.
        turn.pending === true ? "opacity-60" : ""
      }`}
    >
      {parts.map((block, index) => (
        <ChatBlockRenderer
          // Index keys, and deliberately: the blocks of one turn are a fixed ordered list that only
          // ever grows at the end (the streaming bubble is a single text part whose TEXT changes, not
          // whose position does), so an index is stable for exactly as long as it needs to be.
          key={`${turn.id}-${index}`}
          block={block}
          role={isUser ? "user" : "assistant"}
          // The caret and the footers ride on the LAST part only. A caret after the first of three
          // blocks would claim that block is still growing when the turn has moved past it.
          inlineTrailing={index === parts.length - 1 ? caret : null}
          footer={index === parts.length - 1 ? footer : null}
        />
      ))}
    </div>
  );
}
